/*
  The invoice document.

  Built as a self-contained HTML page and handed to the browser's own
  print engine, which the admin saves as a PDF. That keeps the layout
  identical to the approved sample in docs/invoice-sample.html — real
  selectable text, a correct ₹ glyph, no extra dependency — where a
  client-side PDF library would have meant either a 300KB embedded
  Unicode font or printing "Rs." instead of ₹.

  An invoice is only ever raised AFTER payment (see
  backend/sql/invoices.sql), so this document is a receipt: it states
  what was paid, never what is owed.
*/

import { STORE, missingStoreDetails } from './store';

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/* Customer names and addresses land in this markup. They arrive from a
   public checkout form, so every one of them is escaped — an
   apostrophe should not break the bill and a stray tag should not be
   able to rewrite it. */
const esc = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/* composeAddress() already joins sensible lines with ", ", and those
   lines themselves contain commas — "New Delhi, Delhi - 110016".
   Breaking at every comma therefore shredded one address into six
   short lines and pushed the invoice onto a second page. Letting it
   wrap inside the box is both tidier and safe for rows written before
   that format existed. */
const addressBlock = (address) => esc(address || '');

const fmtDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

/* ------------------------------------------------------------------
   Amount in words, Indian system — lakh and crore, not million.
   A bill that says "Two Thousand Three Hundred Eighty-Two Rupees Only"
   is the line that makes tampering with the figures obvious.
------------------------------------------------------------------ */
const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight',
  'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
  'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = [
  '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy',
  'Eighty', 'Ninety',
];

const underHundred = (n) => {
  if (n < 20) return ONES[n];
  const tens = TENS[Math.floor(n / 10)];
  const unit = n % 10;
  return unit ? `${tens}-${ONES[unit]}` : tens;
};

const underThousand = (n) => {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  return [
    hundreds ? `${ONES[hundreds]} Hundred` : '',
    rest ? underHundred(rest) : '',
  ]
    .filter(Boolean)
    .join(' ');
};

export const amountInWords = (amount) => {
  const total = Math.round(Number(amount) || 0);
  if (total === 0) return 'Zero Rupees Only';

  // Indian grouping: crore, lakh, thousand, then the last three digits.
  const parts = [
    [Math.floor(total / 10000000), 'Crore'],
    [Math.floor((total % 10000000) / 100000), 'Lakh'],
    [Math.floor((total % 100000) / 1000), 'Thousand'],
  ];

  const words = parts
    .filter(([value]) => value > 0)
    .map(([value, label]) => `${underThousand(value)} ${label}`);

  const last = total % 1000;
  if (last) words.push(underThousand(last));

  return `${words.join(' ')} Rupees Only`;
};

/* ------------------------------------------------------------------
   The document
------------------------------------------------------------------ */

const lineRows = (items) =>
  items
    .map((item, index) => {
      const meta = [
        item.planet ? `Ruled by ${esc(item.planet)}` : null,
        item.product_code ? `SKU ${esc(item.product_code)}` : null,
      ]
        .filter(Boolean)
        .join(' · ');

      return `
      <tr>
        <td class="num">${index + 1}</td>
        <td>
          <span class="item-name">${esc(item.product_name || 'Item')}</span>
          ${meta ? `<span class="item-meta">${meta}</span>` : ''}
        </td>
        <td class="qty">${Number(item.quantity) || 0}</td>
        <td class="rate">${inr(item.unit_price)}</td>
        <td class="amt">${inr(item.total_price)}</td>
      </tr>`;
    })
    .join('');

/**
 * The invoice for one order, as a complete HTML page.
 *
 * `order` is a row from public.orders with its order_items attached —
 * exactly what getOrders() in the admin API already returns.
 */
export const invoiceHtml = (order) => {
  const items = order.order_items || [];
  const pieces = items.reduce((n, i) => n + (Number(i.quantity) || 0), 0);

  const discount = Number(order.discount_amount) || 0;
  const shipping = Number(order.shipping_charges) || 0;
  const paid = Number(order.final_amount) || 0;

  const paidOn = fmtDate(order.paid_at || order.invoice_issued_at);
  const method = esc(order.payment_method || 'UPI');
  const reference = order.payment_reference
    ? esc(order.payment_reference)
    : null;

  const missing = missingStoreDetails();

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Invoice ${esc(order.invoice_number || '')} — ${esc(STORE.name)}</title>
<link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600&family=Cormorant+Garamond:wght@300;400;500;600&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: 'Cormorant Garamond', Georgia, 'Times New Roman', serif;
    font-size: 11pt; color: #2A2420; background: #F0EEEA;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }

  /* On screen only — the bar the admin uses to save the file. */
  .toolbar {
    position: sticky; top: 0; z-index: 10;
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px; padding: 12px 20px;
    background: #2A2420; color: #fff;
    font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1.5px;
    text-transform: uppercase;
  }
  .toolbar button {
    padding: 9px 20px; border: 1px solid #fff; border-radius: 3px;
    background: #fff; color: #2A2420;
    font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1.5px;
    text-transform: uppercase; cursor: pointer;
  }
  .warn {
    padding: 10px 20px; background: #FFF6E5; color: #8A6410;
    font-family: 'Cormorant Garamond', serif; font-size: 15px;
  }
  @media print {
    .toolbar, .warn { display: none !important; }
    body { background: #fff; }
    /* A long order legitimately runs past one page. When it does, the
       column headings repeat and nothing is torn in half mid-row. */
    .items thead { display: table-header-group; }
    .items tr, .lower, .signoff, .foot-row { break-inside: avoid; }
  }

  /* Deliberately short of the 297mm page. It only pins the sign-off to
     the bottom of a short invoice; leaving headroom means a slightly
     taller address or an extra line item doesn't spill a footer onto a
     page of its own. */
  .sheet {
    width: 210mm; min-height: 278mm; margin: 0 auto; background: #fff;
    padding: 10mm 13mm 6mm; position: relative;
    display: flex; flex-direction: column;
  }
  @media screen { .sheet { margin: 18px auto; box-shadow: 0 6px 26px rgba(0,0,0,.18); } }

  .head { display: flex; justify-content: space-between; align-items: flex-start;
          padding-bottom: 5mm; border-bottom: 2px solid #2A2420; }
  .brand-name { font-family: 'Cinzel', serif; font-size: 18pt; letter-spacing: 4px; margin: 0 0 2mm; }
  .brand-tag { font-size: 10pt; color: #8B8177; margin: 0 0 2.5mm; letter-spacing: .3px; }
  .brand-contact { font-size: 9pt; line-height: 1.5; color: #5C5248; }
  .doc { text-align: right; flex: 0 0 auto; padding-left: 10mm; }
  .doc-type { font-family: 'Cinzel', serif; font-size: 15pt; letter-spacing: 5px; margin: 0 0 3mm; }
  .doc dl { margin: 0; font-size: 9.5pt; line-height: 1.6; }
  .doc dt { display: inline-block; color: #8B8177; letter-spacing: .5px; }
  .doc dd { display: inline-block; margin: 0 0 0 4mm; font-weight: 600; }

  .parties { display: flex; gap: 8mm; margin: 4mm 0; }
  .party { flex: 1; padding: 4mm 5mm; background: #FBFAF8; border: 1px solid #EFEDE9; }
  .party h3, .pay h3, .terms h3 {
    font-family: 'Cinzel', serif; font-size: 8.5pt; font-weight: 400;
    letter-spacing: 2.5px; text-transform: uppercase; color: #8B8177;
    margin: 0 0 2.5mm; text-align: left;
  }
  .party .who { font-size: 12pt; font-weight: 600; margin: 0 0 2mm; }
  .party p { margin: 0; font-size: 10.5pt; line-height: 1.45; color: #5C5248; }
  .party .line { display: flex; justify-content: space-between; gap: 4mm;
                 font-size: 10.5pt; line-height: 1.65; color: #5C5248; }
  .party .line span:last-child { font-weight: 600; color: #2A2420; text-align: right; }

  .items { width: 100%; border-collapse: collapse; margin-bottom: 3mm; }
  .items thead th {
    font-family: 'Cinzel', serif; font-size: 8.5pt; font-weight: 400;
    letter-spacing: 1.5px; text-transform: uppercase; color: #fff;
    background: #2A2420; padding: 3mm 4mm; text-align: left;
  }
  .items tbody td { padding: 2.6mm 4mm; border-bottom: 1px solid #EFEDE9;
                    font-size: 10.5pt; vertical-align: top; }
  .items .num { width: 10mm; color: #8B8177; }
  .items .qty { width: 18mm; text-align: center; }
  .items .rate, .items .amt { width: 30mm; text-align: right; white-space: nowrap; }
  .items .amt { font-weight: 600; }
  .item-name { font-family: 'Cinzel', serif; font-size: 10pt; letter-spacing: .4px;
               display: block; margin-bottom: 1mm; }
  .item-meta { font-size: 9pt; color: #8B8177; }

  .foot-row { display: flex; gap: 10mm; align-items: flex-start; }
  .words { flex: 1; padding-top: 2mm; }
  .words .label { font-family: 'Cinzel', serif; font-size: 8.5pt; letter-spacing: 2px;
                  text-transform: uppercase; color: #8B8177; display: block; margin-bottom: 2mm; }
  .words p { margin: 0; font-size: 10.5pt; font-style: italic; line-height: 1.5; }

  .totals { flex: 0 0 78mm; }
  .totals .row { display: flex; justify-content: space-between; padding: 1.8mm 4mm;
                 font-size: 10.5pt; color: #5C5248; }
  .totals .row.discount { color: #14663A; }
  .totals .row.grand {
    margin-top: 1.5mm; padding: 3.2mm 4mm; background: #2A2420; color: #fff;
    font-family: 'Cinzel', serif; font-size: 10pt; letter-spacing: 2px;
    text-transform: uppercase; align-items: center;
  }
  .totals .row.grand strong { font-size: 14pt; letter-spacing: 0; }
  .paid-strip {
    margin-top: 1.5mm; padding: 2mm 4mm; background: #E6F4EC;
    border: 1px solid #A9CDBA; color: #14663A;
    font-family: 'Cinzel', serif; font-size: 8.5pt; letter-spacing: 1.5px;
    text-transform: uppercase; text-align: center;
  }

  .lower { display: flex; gap: 8mm; margin-top: 6mm; padding-top: 4mm;
           border-top: 1px solid #EFEDE9; }
  .pay { flex: 0 0 78mm; padding: 4mm 5mm; border: 1px solid #A9CDBA; background: #F5FAF7; }
  .paid-mark {
    display: inline-block; margin: 0 0 2.5mm; padding: 1.2mm 4mm;
    border: 1.5px solid #14663A; color: #14663A;
    font-family: 'Cinzel', serif; font-size: 12pt; letter-spacing: 4px;
  }
  .pay p { margin: 0 0 1.6mm; font-size: 10pt; line-height: 1.4; color: #5C5248; }
  .pay .due { display: flex; justify-content: space-between; margin-top: 2.5mm;
              padding-top: 2.5mm; border-top: 1px solid #CFE3D8; font-size: 11pt; }
  .pay .due strong { font-size: 13pt; }

  .terms { flex: 1; }
  .terms ol { margin: 0; padding-left: 5mm; font-size: 9.5pt; line-height: 1.45; color: #5C5248; }

  .signoff { margin-top: auto; padding-top: 3mm; display: flex;
             justify-content: space-between; align-items: flex-end; }
  .thanks { font-size: 11pt; font-style: italic; color: #5C5248; margin: 0; }
  .thanks span { display: block; font-style: normal; font-size: 9.5pt;
                 color: #8B8177; margin-top: 1.5mm; }
  .sign { text-align: center; font-size: 9.5pt; color: #8B8177; }
  .sign .rule { width: 50mm; border-bottom: 1px solid #C7BFB5; margin-bottom: 2mm; height: 6mm; }

  .strip { margin-top: 3mm; padding-top: 2.5mm; border-top: 2px solid #2A2420;
           display: flex; justify-content: space-between; font-size: 9pt;
           color: #8B8177; letter-spacing: .4px; }
</style>
</head>
<body>
<div class="toolbar">
  <span>Invoice ${esc(order.invoice_number || '')} · ${esc(order.customer_name || '')}</span>
  <button onclick="window.print()">Save as PDF</button>
</div>
${
  missing.length
    ? `<div class="warn">Still using placeholder details for the
       ${missing.map(esc).join(' and ')} — set them in
       <code>src/utils/store.js</code> before sending this to a customer.</div>`
    : ''
}

<div class="sheet">
  <header class="head">
    <div>
      <h1 class="brand-name">${esc(STORE.name).toUpperCase()}</h1>
      <p class="brand-tag">${esc(STORE.tagline)}</p>
      <div class="brand-contact">
        ${STORE.addressLines.map(esc).join('<br />')}<br />
        WhatsApp ${esc(STORE.phone)} &nbsp;·&nbsp; ${esc(STORE.email)}
        ${STORE.gstin ? `<br />GSTIN ${esc(STORE.gstin)}` : ''}
      </div>
    </div>

    <div class="doc">
      <h2 class="doc-type">INVOICE</h2>
      <dl>
        <dt>Invoice No</dt><dd>${esc(order.invoice_number || '—')}</dd><br />
        <dt>Invoice Date</dt><dd>${fmtDate(order.invoice_issued_at)}</dd><br />
        <dt>Order No</dt><dd>${esc(order.order_number || '—')}</dd><br />
        <dt>Order Date</dt><dd>${fmtDate(order.created_at)}</dd>
      </dl>
    </div>
  </header>

  <section class="parties">
    <div class="party">
      <h3>Billed &amp; Shipped To</h3>
      <p class="who">${esc(order.customer_name || '—')}</p>
      <p>
        ${addressBlock(order.shipping_address) || '—'}<br />
        Phone ${esc(order.customer_phone || '—')}
      </p>
    </div>

    <div class="party">
      <h3>Order Summary</h3>
      <div class="line"><span>Items</span><span>${pieces} piece${pieces === 1 ? '' : 's'}</span></div>
      <div class="line"><span>Payment method</span><span>${method}</span></div>
      <div class="line"><span>Payment date</span><span>${paidOn}</span></div>
      ${reference ? `<div class="line"><span>UPI reference</span><span>${reference}</span></div>` : ''}
      <div class="line"><span>Placed via</span><span>Website · WhatsApp</span></div>
    </div>
  </section>

  <table class="items">
    <thead>
      <tr>
        <th class="num">#</th><th>Item</th><th class="qty">Qty</th>
        <th class="rate">Rate</th><th class="amt">Amount</th>
      </tr>
    </thead>
    <tbody>${lineRows(items)}</tbody>
  </table>

  <div class="foot-row">
    <div class="words">
      <span class="label">Amount in words</span>
      <p>${esc(amountInWords(paid))}</p>
    </div>

    <div class="totals">
      <div class="row"><span>Subtotal</span><span>${inr(order.total_amount)}</span></div>
      ${
        discount > 0
          ? `<div class="row discount"><span>Discount${
              order.coupon_code ? ` (${esc(order.coupon_code)})` : ''
            }</span><span>− ${inr(discount)}</span></div>`
          : ''
      }
      <div class="row"><span>Shipping</span><span>${shipping > 0 ? inr(shipping) : 'Free'}</span></div>
      <div class="row grand"><span>Total Paid</span><strong>${inr(paid)}</strong></div>
      <div class="paid-strip">Paid in full · ${method} · ${paidOn}</div>
    </div>
  </div>

  <section class="lower">
    <div class="pay">
      <h3>Payment Received</h3>
      <p class="paid-mark">PAID</p>
      <p>Received in full by ${method} on <strong>${paidOn}</strong>.</p>
      <p>Paid to ${esc(STORE.name)} · <strong>${esc(STORE.upiId)}</strong>
        ${reference ? `<br />${method} reference <strong>${reference}</strong>` : ''}
      </p>
      <div class="due"><span>Amount paid</span><strong>${inr(paid)}</strong></div>
    </div>

    <div class="terms">
      <h3>Terms</h3>
      <ol>
        <li>Payment for this order has been received in full. Nothing further is due.</li>
        <li>7-day returns on unused items in original packaging, tags and certificates intact.</li>
        <li>An unboxing video is required for damage or wrong-item claims.</li>
        <li>Every stone is lab-certified; the certificate ships with the piece.</li>
        <li>Queries about this invoice: WhatsApp ${esc(STORE.phone)}.</li>
      </ol>
    </div>
  </section>

  <div class="signoff">
    <p class="thanks">Thank you for your order.
      <span>May these stones sit well with your chart.</span></p>
    <div class="sign"><div class="rule"></div>For ${esc(STORE.name)}</div>
  </div>

  <div class="strip">
    <span>${esc(STORE.name).toUpperCase()} · ${esc(STORE.addressLines.join(', ')).toUpperCase()}</span>
    <!-- No page counter: a long order genuinely runs to a second page,
         and CSS can't count printed pages, so "Page 1 of 1" would be a
         lie on exactly the invoices where it mattered. -->
    <span>Invoice ${esc(order.invoice_number || '')}</span>
  </div>
</div>
</body>
</html>`;
};

/**
 * Opens the invoice in its own tab, ready to save as a PDF.
 *
 * Must be called straight from a click — a popup opened later, from a
 * promise callback, is what browsers block.
 */
export const openInvoice = (order) => {
  const win = window.open('', '_blank');

  if (!win) {
    return {
      ok: false,
      error:
        'Your browser blocked the invoice window. Allow pop-ups for this ' +
        'site and try again.',
    };
  }

  win.document.write(invoiceHtml(order));
  win.document.close();
  return { ok: true };
};
